import React, { useEffect, useState } from 'react';

const MultiplayerFinishedScreen = ({ 
  score, 
  gameData, 
  players, 
  playerName, 
  resetGame,
  onRefreshLobby // Add this prop for live updates
}) => {
  const [refreshInterval, setRefreshInterval] = useState(null);
  // Log component lifecycle
  useEffect(() => {
    console.log('[MultiplayerFinishedScreen] Component mounted', {
      score,
      totalQuestions: gameData?.questionCount,
      percentage: gameData ? Math.round((score / gameData.questionCount) * 100) : 0,
      gameId: gameData?.gameId,
      playersCount: players?.length || 0,
      playerName,
      players: players?.map(p => ({ playerName: p.playerName, score: p.score, completed: p.completed }))
    });
  }, [score, gameData, players, playerName]);

  // Set up auto-refresh for live leaderboard updates
  useEffect(() => {
    if (onRefreshLobby) {
      console.log('[MultiplayerFinishedScreen] Setting up auto-refresh for live leaderboard updates');

      // Refresh immediately
      onRefreshLobby();

      // Set up interval to refresh every 2 seconds for live updates
      const interval = setInterval(() => {
        console.log('[MultiplayerFinishedScreen] Auto-refreshing for live leaderboard updates');
        onRefreshLobby();
      }, 2000);

      setRefreshInterval(interval);

      // Cleanup interval on unmount
      return () => {
        console.log('[MultiplayerFinishedScreen] Cleaning up refresh interval');
        clearInterval(interval);
      };
    }
  }, [onRefreshLobby]);

  const handleNewGame = () => {
    console.log('[MultiplayerFinishedScreen] New game button clicked - returning to main menu', {
      previousScore: score,
      previousGameId: gameData?.gameId,
      playerName
    });
    resetGame();
  };

  // Filter to only completed players and sort by score (descending)
  const completedPlayers = (players || []).filter(player => player.completed);
  const sortedPlayers = [...completedPlayers].sort((a, b) => b.score - a.score);
  const playerRank = sortedPlayers.findIndex(p => p.playerName === playerName) + 1;
  const totalPlayers = sortedPlayers.length;
  
  // Check if all players have finished the game
  const allPlayersCompleted = players && players.length > 0 && players.every(player => player.completed);
  const totalPlayersInGame = players ? players.length : 0;

  // Debug logging
  console.log('[MultiplayerFinishedScreen] Player filtering:', {
    allPlayers: players?.map(p => ({ playerName: p.playerName, completed: p.completed })),
    completedPlayers: completedPlayers.map(p => ({ playerName: p.playerName, completed: p.completed })),
    sortedPlayers: sortedPlayers.map(p => ({ playerName: p.playerName, score: p.score })),
    allPlayersCompleted,
    totalPlayersInGame
  });

  return (
    <div className="game-card">
      <h1>
        <span role="img" aria-label="celebration">
          {allPlayersCompleted ? "🎉" : "🎯"}
        </span> 
        {allPlayersCompleted ? "Game Complete!" : "You Finished!"}
      </h1>
      
      <div className="final-results">
        <div className="personal-score">
          <h2>Your Score: {score} / {gameData.questionCount}</h2>
          <div className="rank-display">
            You finished #{playerRank} of {totalPlayers} players!
          </div>
          <div className="percentage">
            {Math.round((score / gameData.questionCount) * 100)}% correct
          </div>
        </div>
        
        <div className="leaderboard">
          <h3>Final Leaderboard</h3>
          <div className="leaderboard-list">
            {sortedPlayers.map((player, index) => (
              <div 
                key={player.userId || index} 
                className={`leaderboard-item ${player.playerName === playerName ? 'current-player' : ''}`}
              >
                <div className="rank">
                  {index === 0 && <span role="img" aria-label="trophy">🏆</span>}
                  {index === 1 && <span role="img" aria-label="medal">🥈</span>}
                  {index === 2 && <span role="img" aria-label="medal">🥉</span>}
                  {index > 2 && `#${index + 1}`}
                </div>
                <div className="player-info">
                  <span className="player-name">{player.playerName}</span>
                  <span className="player-score">{player.score}/{gameData.questionCount}</span>
                </div>
                <div className="percentage">
                  {Math.round((player.score / gameData.questionCount) * 100)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="performance-message">
        {allPlayersCompleted ? (
          // Show congratulations only when all players have finished
          playerRank === 1 
            ? "🏆 Congratulations! You won the game!" 
            : playerRank === 2
            ? "🥈 Great job! You came in second place!"
            : playerRank === 3
            ? "🥉 Nice work! You made it to third place!"
            : score >= gameData.questionCount * 0.8 
            ? "🌟 Excellent performance! You did really well!" 
            : score >= gameData.questionCount * 0.6 
            ? "👍 Good work! Keep practicing!" 
            : "💪 Keep trying! Practice makes perfect!"
        ) : (
          // Show waiting message when other players are still playing
          <div>
            <p>🎯 You've finished! Great job!</p>
            <p>⏳ Waiting for other players to finish...</p>
            <p><small>({completedPlayers.length} of {totalPlayersInGame} players completed)</small></p>
          </div>
        )}
      </div>
      
      <button className="start-button" onClick={handleNewGame}>
        New Game
      </button>
    </div>
  );
};

export default MultiplayerFinishedScreen;
